import React, { FormEvent, useState, useEffect } from 'react';
import useTranslate from '@src/i18n/useTranslate';
import DotFalling from '@src/components/reusable/spinner/DotFalling';
import { usePasswordRecovery } from '@src/features/customers/auth/password-recovery/hooks/usePasswordRecovery';
import { useAppDispatch } from '@src/core/hooks';
import { setErrors } from '@src/features/customers/auth/store/auth';
import styled from 'styled-components';
import { BodyRegular } from '@src/fonts/typography/Typography';
import {
  AuthContainer,
  AuthForm,
  AuthHeader,
  AuthHeaderTitle,
  AuthHeaderSubtitle,
  AuthButtonGroup,
  AuthPrimaryButton,
  AuthPreviousStepButton,
} from '@src/features/customers/auth/legacy/styled/AuthStyled';
import { LayoutColors } from '@src/core/theme';
import { RECOVERY_STEP } from '@src/features/customers/types';
import InputCode from '@src/features/customers/auth/legacy/inputs/InputCode';
import { track } from '@src/services/analytics';

interface ValidCodeProps {
  onBack: () => void;
}
const ValidCode = ({ onBack }: ValidCodeProps) => {
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const { validateCode, openResendPrompt, loading, recoveryEmail: passwordRecoveryEmail } = usePasswordRecovery();
  const [codeOTP, setCodeOTP] = useState('');

  useEffect(() => {
    dispatch(setErrors(RECOVERY_STEP.VALID_CODE, {}));

    track('recoveryPasswordCodeStepAccess');
  }, []);

  const handleResendEmail = () => {
    openResendPrompt();
  };

  const handleSubmit = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    track('recoveryPasswordCodeStepSubmit');

    validateCode(passwordRecoveryEmail ?? '', codeOTP);
  };

  return (
    <AuthContainer>
      <AuthForm key={RECOVERY_STEP.VALID_CODE} onSubmit={handleSubmit}>
        <AuthHeader>
          <AuthHeaderTitle>{i18n('auth_recoverPassword')}</AuthHeaderTitle>
          <AuthHeaderSubtitle>
            {i18n('auth_validCodeSubtitle')}
            <strong> {passwordRecoveryEmail}</strong>
          </AuthHeaderSubtitle>
          <InputCode
            label={i18n('auth_code')}
            errorContext={RECOVERY_STEP.VALID_CODE}
            setCodeOTP={setCodeOTP}
            codeOTP={codeOTP}
          />
          <AuthButtonGroup>
            <AuthPrimaryButton>{loading ? <DotFalling /> : i18n('auth_continue')}</AuthPrimaryButton>
            <InfoResendEmail>
              {i18n('auth_didNotGetTheEmail')}
              <LinkResendEmail onClick={handleResendEmail}>{i18n('auth_clickHere')}</LinkResendEmail>
            </InfoResendEmail>
            <AuthPreviousStepButton onClick={() => onBack()}>{i18n('auth_goBackButton')}</AuthPreviousStepButton>
          </AuthButtonGroup>
        </AuthHeader>
      </AuthForm>
    </AuthContainer>
  );
};

const InfoResendEmail = styled.div`
  ${BodyRegular};
  display: block;
  width: 100%;
`;

const LinkResendEmail = styled.a`
  font-size: inherit;
  color: ${LayoutColors.justBlue};
  padding-left: 5px;
  text-decoration: none;
  cursor: pointer;
`;

export default ValidCode;
